import { createContext, type Dispatch, type ReactNode, useEffect, useReducer } from 'react'
import { useAuth } from '@/store/auth/useAuth'
import { type UserDataAction, UserDataActionTypes, type UserDataState } from './types'
import { initialUserDataState, userDataReducer } from './userDataReducer'

interface UserDataContextValue {
  state: UserDataState
  dispatch: Dispatch<UserDataAction>
}

export const UserDataContext = createContext<UserDataContextValue | undefined>(undefined)

interface UserDataProviderProps {
  children: ReactNode
}

export function UserDataProvider({ children }: UserDataProviderProps) {
  const [state, dispatch] = useReducer(userDataReducer, initialUserDataState)
  const { user, isAuthenticated, initialized: authInitialized } = useAuth()

  useEffect(() => {
    if (!authInitialized) return

    if (isAuthenticated && user) {
      dispatch({ type: UserDataActionTypes.USER_DATA_INIT, payload: user.uid })
      // With no data-type subscriptions yet, immediately mark ready.
      // When data-type contexts are added, they each manage their own loading state.
      dispatch({ type: UserDataActionTypes.USER_DATA_READY })
    } else {
      dispatch({ type: UserDataActionTypes.USER_DATA_RESET })
    }
  }, [isAuthenticated, user, authInitialized])

  return <UserDataContext.Provider value={{ state, dispatch }}>{children}</UserDataContext.Provider>
}
